import {COMMON, TYPOGRAPHY, get} from '@fuegokit/react/constants'
import sx, {SxProp} from '@fuegokit/react/sx'
import {FuegokitThemeTypes} from '@fuegokit/react/utils/theme-get'
import {ComponentProps} from '@fuegokit/react/utils/types'
import styled from 'styled-components'
import {variant} from 'styled-system'

export const getLineHeight = (sizePx: number, lineHeightPx: number) => lineHeightPx / sizePx

// @TODO: deprecate variants API
const variants = variant({
  variants: {
    h100: {
      fontSize: '11px',
      fontWeight: 700,
      letterSpacing: 0,
      lineHeight: getLineHeight(11, 16),
      textTransform: 'none'
    },
    h200: {
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: 0,
      lineHeight: getLineHeight(12, 16),
      textTransform: 'none'
    },
    h300: {
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: 0,
      lineHeight: getLineHeight(12, 16),
      textTransform: 'uppercase'
    },
    h400: {
      fontSize: '14px',
      fontWeight: 600,
      letterSpacing: '-0.003em',
      lineHeight: getLineHeight(14, 16),
      textTransform: 'none'
    },
    h500: {
      fontSize: '16px',
      fontWeight: 600,
      letterSpacing: '-0.003em',
      lineHeight: getLineHeight(16, 20),
      textTransform: 'none'
    },
    h600: {
      fontSize: '20px',
      fontWeight: 500,
      letterSpacing: '-0.008em',
      lineHeight: getLineHeight(20, 24),
      textTransform: 'none'
    },
    h700: {
      fontSize: '24px',
      fontWeight: 500,
      letterSpacing: '-0.01em',
      lineHeight: getLineHeight(24, 28),
      textTransform: 'none'
    },
    h800: {
      fontSize: '29px',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: getLineHeight(29, 32),
      textTransform: 'none'
    },
    h900: {
      fontSize: '35px',
      fontWeight: 500,
      letterSpacing: '-0.01em',
      lineHeight: getLineHeight(35, 40),
      textTransform: 'none'
    }
  }
})

type HeadingBaseProps = {
  /**
   * Allows the component to be rendered as the specified DOM Element
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  /**
   * The heading level in the product environment
   */

  variant?: 'h900' | 'h800' | 'h700' | 'h600' | 'h500' | 'h400' | 'h300' | 'h200' | 'h100'
  /**
   * Optional id for automated tests
   */
  testId?: string
}

const HeadingBase = styled.h2<HeadingBaseProps>`
  ${COMMON}
  ${TYPOGRAPHY}
  ${variants}
`
/**
 *
 * The Heading component is a typography component used to display text in different sizes and formats.
 *
 * Fuegokit's Heading component renders an html `h2` tag with the "h800" styles applied by default, including color.
 *
 * It should serve as a drop-in component consistent with the host environment wherever you need to render a heading.
 * Should it be necessary, the `sx` prop can be provided a value to over-ride the component styles.
 *
 * ```jsx
 *
 * const MyComponent = ({props}) => {
 *  return (
 *    <div {...props}>
 *      <Heading
 *        as='h2'
 *        variant='h800'
 *        sx={{ color: 'tomato', fontWeight: 700 }}
 *      >Maybe I will, maybe I won't.
 *      </Heading>
 *      {/ ...rest of your component /}
 *    </div>
 *  )
 * }
 *
 * ```
 *
 * Heading levels `h1` - `h6` are available.
 *
 * When passing a semantic element, it should be done in a way that is consistent with the component's
 * intended use, such as cases where accessibility will be improved by using semantically appropriate HTML elements.
 *
 * When working in the product environment Fuegokit's Heading component is a drop-in replacement
 * for Atlaskit's Heading component, with other heading levels available through the `level` prop
 * that adhere to the product experience spec in the host environment.
 *
 * It has a similar API to Atlaskit's Heading component, but with one crucial difference: it
 * does not render margin. Margin breaks encapsulation and makes reusability harder;
 * good components should be usable in any context or layout in surprising and hard to manage ways.
 *
 * In cases that you need vertical margin, consider using a layout component like `<Spacer>` instead.
 *
 * Please reference the [w3 recommendations for headings](https://www.w3.org/WAI/tutorials/page-structure/headings/)
 * to ensure your headings provide an accessible experience for screen readers.
 */
const variantMapping: {[k: string]: FuegokitThemeTypes} = {
  h100: 'colors.text.subtle',
  h200: 'colors.text.subtle',
  h300: 'colors.text.default',
  h400: 'colors.text.default',
  h500: 'colors.text.default',
  h600: 'colors.text.default',
  h700: 'colors.text.default',
  h800: 'colors.text.default',
  h900: 'colors.text.default'
}
const Heading = styled(HeadingBase)<HeadingBaseProps & SxProp>`
  color: ${p => (p.variant && {}.hasOwnProperty.call(variantMapping, p.variant) ? get(variantMapping[p.variant]) : '')};
  ${sx}
`

export default Heading
export type HeadingProps = ComponentProps<typeof Heading>
Heading.defaultProps = {
  variant: 'h800',
  as: 'h2'
}
